import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  companiesAPI, 
  objectivesAPI, 
  keyResultsAPI, 
  tasksAPI, 
  notesAPI,
  aiAPI 
} from '../lib/api';
import { TrafficLight } from '../components/TrafficLight';
import TaskDetailModal from '../components/TaskDetailModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Sparkles, 
  Target, 
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  MessageSquare,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const ScorecardPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { isOwner, isExecutive } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [keyResults, setKeyResults] = useState({});
  const [tasks, setTasks] = useState({});
  const [notes, setNotes] = useState({});
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [expandedKRs, setExpandedKRs] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState({});
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [showWeeklyPlanDialog, setShowWeeklyPlanDialog] = useState(false);
  
  // Task detail modal
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
  
  // Task creation dialog
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [selectedKR, setSelectedKR] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    effort: 'M',
    priority: 'medium',
    definition_of_done: ''
  });

  // Note creation dialog
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [selectedKRForNote, setSelectedKRForNote] = useState(null);
  const [newNote, setNewNote] = useState({
    note_type: 'evidence',
    content: ''
  });

  // Objective creation dialog
  const [showObjectiveDialog, setShowObjectiveDialog] = useState(false);
  const [generatingObjectives, setGeneratingObjectives] = useState(false);
  const [aiObjectives, setAiObjectives] = useState([]);
  const [newObjective, setNewObjective] = useState({
    title: '',
    description: '',
    quarter: 'Q1',
    year: 2026,
    visibility: 'public'
  });
  const [objectiveFocusArea, setObjectiveFocusArea] = useState('');

  // KR creation dialog
  const [showKRDialog, setShowKRDialog] = useState(false);
  const [selectedObjectiveForKR, setSelectedObjectiveForKR] = useState(null);
  const [generatingKRs, setGeneratingKRs] = useState(false);
  const [aiKRs, setAiKRs] = useState([]);
  const [newKR, setNewKR] = useState({
    title: '',
    description: '',
    target_value: 100,
    current_value: 0,
    unit: '%',
    visibility: 'public'
  });

  const handleTaskClick = (task) => {
    setSelectedTaskForDetail(task);
    setShowTaskDetailModal(true);
  };

  const handleTaskUpdate = (updatedTask) => {
    setTasks(prev => ({
      ...prev,
      [updatedTask.kr_id]: prev[updatedTask.kr_id]?.map(t => 
        t.task_id === updatedTask.task_id ? updatedTask : t
      )
    }));
    setSelectedTaskForDetail(updatedTask);
  };

  const handleTaskDelete = (deletedTaskId) => {
    // Remove task from state
    setTasks(prev => {
      const newTasks = { ...prev };
      Object.keys(newTasks).forEach(krId => {
        newTasks[krId] = newTasks[krId]?.filter(t => t.task_id !== deletedTaskId);
      });
      return newTasks;
    });
    setSelectedTaskForDetail(null);
    setShowTaskDetailModal(false);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [companyRes, objectivesRes] = await Promise.all([
        companiesAPI.getOne(companyId),
        objectivesAPI.getAll(companyId)
      ]);
      
      setCompany(companyRes.data);
      setObjectives(objectivesRes.data);

      // Initialize expanded state
      const expanded = {};
      objectivesRes.data.forEach(obj => {
        expanded[obj.objective_id] = true;
      });
      setExpandedObjectives(expanded);

      // Fetch KRs for each objective
      const krsMap = {};
      const tasksMap = {};
      const notesMap = {};
      const expandedKRsState = {};

      for (const obj of objectivesRes.data) {
        const krsRes = await keyResultsAPI.getAll({ objective_id: obj.objective_id });
        krsMap[obj.objective_id] = krsRes.data;
        
        for (const kr of krsRes.data) {
          const [tasksRes, notesRes] = await Promise.all([
            tasksAPI.getAll({ kr_id: kr.kr_id }),
            notesAPI.getAll({ kr_id: kr.kr_id })
          ]);
          tasksMap[kr.kr_id] = tasksRes.data;
          notesMap[kr.kr_id] = notesRes.data;
          expandedKRsState[kr.kr_id] = false;
        }
      }
      
      setKeyResults(krsMap);
      setTasks(tasksMap);
      setNotes(notesMap);
      setExpandedKRs(expandedKRsState);
    } catch (error) {
      console.error('Error fetching scorecard:', error);
      toast.error('Error al cargar scorecard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleGenerateAITasks = async (krId) => {
    try {
      setGeneratingAI(prev => ({ ...prev, [krId]: true }));
      const response = await aiAPI.generateTasks(krId);
      toast.success(`${response.data.drafts.length} borradores generados`);
    } catch (error) {
      toast.error('Error al generar tareas');
    } finally {
      setGeneratingAI(prev => ({ ...prev, [krId]: false }));
    }
  };

  const handleWeeklyPlan = async () => {
    try {
      setGeneratingAI(prev => ({ ...prev, weekly: true }));
      const response = await aiAPI.weeklyPlan(companyId);
      toast.success('Plan semanal generado');
      // Show the plan in a modal or navigate
      console.log('Weekly plan:', response.data.plan);
      setWeeklyPlan(response.data.plan);
      setShowWeeklyPlanDialog(true);
    } catch (error) {
      console.error('Weekly plan error:', error);
      toast.error('Error al generar plan semanal');
    } finally {
      setGeneratingAI(prev => ({ ...prev, weekly: false }));
    }
  };

  const handleCreateTask = async () => {
    if (!selectedKR || !newTask.title) return;
    
    try {
      await tasksAPI.create({
        kr_id: selectedKR.kr_id,
        company_id: companyId,
        ...newTask
      });
      toast.success('Tarea creada');
      setShowTaskDialog(false);
      setNewTask({ title: '', description: '', effort: 'M', priority: 'medium', definition_of_done: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear tarea');
    }
  };

  const handleCreateNote = async () => {
    if (!selectedKRForNote || !newNote.content) return;
    
    try {
      await notesAPI.create({
        kr_id: selectedKRForNote.kr_id,
        company_id: companyId,
        ...newNote
      });
      toast.success('Nota creada');
      setShowNoteDialog(false);
      setNewNote({ note_type: 'evidence', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear nota');
    }
  };

  // Objective handlers
  const handleGenerateAIObjectives = async () => {
    try {
      setGeneratingObjectives(true);
      const response = await aiAPI.generateObjectives(companyId, objectiveFocusArea, 3);
      setAiObjectives(response.data.objectives || []);
      toast.success(`${response.data.objectives?.length || 0} objetivos generados con IA`);
    } catch (error) {
      toast.error('Error al generar objetivos');
    } finally {
      setGeneratingObjectives(false);
    }
  };

  const handleCreateObjective = async (objectiveData = null) => {
    const dataToCreate = objectiveData || newObjective;
    if (!dataToCreate.title) {
      toast.error('El título es requerido');
      return;
    }
    
    try {
      await objectivesAPI.create({
        company_id: companyId,
        ...dataToCreate
      });
      toast.success('Objetivo creado');
      setShowObjectiveDialog(false);
      setNewObjective({ title: '', description: '', quarter: 'Q1', year: 2026, visibility: 'public' });
      setAiObjectives([]);
      setObjectiveFocusArea('');
      fetchData();
    } catch (error) {
      toast.error('Error al crear objetivo');
    }
  };

  // KR handlers
  const handleGenerateAIKRs = async () => {
    if (!selectedObjectiveForKR) return;
    
    try {
      setGeneratingKRs(true);
      const response = await aiAPI.generateKeyResults(selectedObjectiveForKR.objective_id, 3);
      setAiKRs(response.data.key_results || []);
      toast.success(`${response.data.key_results?.length || 0} Key Results generados con IA`);
    } catch (error) {
      toast.error('Error al generar Key Results');
    } finally {
      setGeneratingKRs(false);
    }
  };

  const handleCreateKR = async (krData = null) => {
    if (!selectedObjectiveForKR) return;
    
    const dataToCreate = krData || newKR;
    if (!dataToCreate.title) {
      toast.error('El título es requerido');
      return;
    }
    
    try {
      await keyResultsAPI.create({
        objective_id: selectedObjectiveForKR.objective_id,
        company_id: companyId,
        ...dataToCreate
      });
      toast.success('Key Result creado');
      setShowKRDialog(false);
      setNewKR({ title: '', description: '', target_value: 100, current_value: 0, unit: '%', visibility: 'public' });
      setAiKRs([]);
      fetchData();
    } catch (error) {
      toast.error('Error al crear Key Result');
    }
  };

  const handleUpdateKRProgress = async (krId, currentValue) => {
    try {
      await keyResultsAPI.update(krId, { current_value: parseFloat(currentValue) });
      toast.success('Progreso actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return 'green';
      case 'at_risk': return 'yellow';
      case 'blocked': return 'red';
      default: return 'gray';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'blocker': return 'text-red-700 bg-red-50 border-red-200';
      case 'evidence': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      case 'proposal': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'decision': return 'text-amber-700 bg-amber-50 border-amber-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scorecard-page">
      {/* Header - Fitts's Law: Back button easily accessible */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button 
          onClick={() => navigate('/dashboard')}
          className="icon-button self-start"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">{company?.code}</span>
          <h1 className="text-xl font-semibold text-gray-900">
            {company?.name}
          </h1>
        </div>
        {/* Fitts's Law: Primary action button large and prominent */}
        <div className="flex gap-2">
          <Button 
            onClick={handleWeeklyPlan}
            disabled={generatingAI.weekly}
            className="action-button-primary bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
            data-testid="generate-weekly-plan-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {generatingAI.weekly ? 'Generando...' : 'Plan Semanal (IA)'}
          </Button>
        </div>
      </div>

      {/* Miller's Law: Show summary count of objectives */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Target className="w-4 h-4" />
          <span>{objectives.length} objetivos</span>
          <span className="text-gray-300">•</span>
          <span>{Object.values(keyResults).flat().length} key results</span>
        </div>
        <Button
          onClick={() => setShowObjectiveDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-objective-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Objetivo
        </Button>
      </div>

      {/* Objectives - Miller's Law: Collapsible sections for managing complexity */}
      <div className="space-y-4">
        {objectives.map((objective) => (
          <div key={objective.objective_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Objective Header - Fitts's Law: Large clickable header */}
            <button
              className="w-full p-5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left min-h-[64px]"
              onClick={() => setExpandedObjectives(prev => ({
                ...prev,
                [objective.objective_id]: !prev[objective.objective_id]
              }))}
              data-testid={`objective-${objective.objective_id}`}
            >
              {expandedObjectives[objective.objective_id] ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
              <Target className="w-5 h-5 text-blue-500" />
              <span className="font-medium text-gray-900 flex-1">{objective.title}</span>
              <span className="text-sm text-gray-500">
                {keyResults[objective.objective_id]?.length || 0} KRs
              </span>
            </button>

            {/* Key Results */}
            {expandedObjectives[objective.objective_id] && (
              <div className="border-t border-gray-100">
                {keyResults[objective.objective_id]?.map((kr) => {
                  const progress = kr.target_value > 0 
                    ? Math.round((kr.current_value / kr.target_value) * 100) 
                    : 0;
                  
                  return (
                    <div 
                      key={kr.kr_id}
                      className="border-b border-gray-50 last:border-b-0"
                    >
                      {/* KR Header */}
                      <div className="p-4 pl-12 flex flex-col sm:flex-row sm:items-center gap-3">
                        <button
                          className="flex items-center gap-2 self-start"
                          onClick={() => setExpandedKRs(prev => ({
                            ...prev,
                            [kr.kr_id]: !prev[kr.kr_id]
                          }))}
                        >
                          {expandedKRs[kr.kr_id] ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        
                        <TrafficLight status={getStatusColor(kr.status)} />
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700">{kr.title}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="h-2 bg-gray-200 rounded-full w-32 overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full transition-all"
                                style={{ width: `${Math.min(progress, 100)}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm text-gray-500">
                              <span className="text-gray-900 font-medium">{kr.current_value}</span>
                              /{kr.target_value} {kr.unit}
                            </span>
                            <span className="font-mono text-sm text-cyan-400">{progress}%</span>
                            {getTrendIcon(kr.trend)}
                          </div>
                        </div>

                        {/* Quick Update */}
                        {isExecutive && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              className="w-20 h-8 bg-gray-50 border-gray-200 text-center font-mono text-sm focus:bg-white focus:border-gray-300"
                              defaultValue={kr.current_value}
                              onBlur={(e) => {
                                if (e.target.value !== kr.current_value.toString()) {
                                  handleUpdateKRProgress(kr.kr_id, e.target.value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateKRProgress(kr.kr_id, e.target.value);
                                }
                              }}
                              data-testid={`kr-input-${kr.kr_id}`}
                            />
                            
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedKR(kr);
                                setShowTaskDialog(true);
                              }}
                              data-testid={`add-task-${kr.kr_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedKRForNote(kr);
                                setShowNoteDialog(true);
                              }}
                              data-testid={`add-note-${kr.kr_id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-cyan-400"
                              onClick={() => handleGenerateAITasks(kr.kr_id)}
                              disabled={generatingAI[kr.kr_id]}
                              data-testid={`generate-tasks-${kr.kr_id}`}
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              {generatingAI[kr.kr_id] ? 'Generando...' : 'IA'}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Expanded KR Content */}
                      {expandedKRs[kr.kr_id] && (
                        <div className="pl-20 pr-4 pb-4 space-y-4">
                          {/* Tasks */}
                          {tasks[kr.kr_id]?.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Tareas</span>
                              <div className="space-y-2">
                                {tasks[kr.kr_id].map((task) => (
                                  <div 
                                    key={task.task_id}
                                    onClick={() => handleTaskClick(task)}
                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 cursor-pointer transition-colors group"
                                    data-testid={`task-item-${task.task_id}`}
                                  >
                                    {task.status === 'completed' ? (
                                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    ) : task.status === 'blocked' ? (
                                      <AlertTriangle className="w-4 h-4 text-red-500" />
                                    ) : (
                                      <Clock className="w-4 h-4 text-gray-400" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <span className={`text-sm block ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                        {task.title}
                                      </span>
                                      {/* Progress Bar */}
                                      <div className="mt-1.5 flex items-center gap-2">
                                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className={`h-full transition-all duration-300 ${
                                              (task.progress || 0) >= 80 ? 'bg-emerald-500' :
                                              (task.progress || 0) >= 50 ? 'bg-blue-500' :
                                              (task.progress || 0) >= 25 ? 'bg-amber-500' :
                                              'bg-gray-300'
                                            }`}
                                            style={{ width: `${task.progress || 0}%` }}
                                          />
                                        </div>
                                        <span className="text-xs text-gray-500 font-medium w-8">{task.progress || 0}%</span>
                                      </div>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                      task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {task.priority}
                                    </span>
                                    <span className="text-xs text-gray-500 font-mono">{task.effort}</span>
                                    <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                      Ver detalles →
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {notes[kr.kr_id]?.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Notas</span>
                              <div className="space-y-2">
                                {notes[kr.kr_id].map((note) => (
                                  <div 
                                    key={note.note_id}
                                    className={`p-3 rounded-lg border ${getNoteTypeColor(note.note_type)}`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs uppercase font-medium">{note.note_type}</span>
                                    </div>
                                    <p className="text-sm text-gray-700">{note.content}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Add KR Button */}
                {isExecutive && (
                  <div className="p-4 pl-12 border-t border-gray-100">
                    <Button
                      onClick={() => {
                        setSelectedObjectiveForKR(objective);
                        setShowKRDialog(true);
                      }}
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`add-kr-${objective.objective_id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Key Result
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Nueva Tarea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <span className="text-sm text-gray-500 mb-2 block">
                KR: {selectedKR?.title}
              </span>
            </div>
            <div>
              <Input
                placeholder="Título de la tarea"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Textarea
                placeholder="Descripción"
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select 
                value={newTask.effort} 
                onValueChange={(v) => setNewTask(prev => ({ ...prev, effort: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Esfuerzo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">S (Pequeño)</SelectItem>
                  <SelectItem value="M">M (Mediano)</SelectItem>
                  <SelectItem value="L">L (Grande)</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={newTask.priority} 
                onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Prioridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="low">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Textarea
                placeholder="Definition of Done"
                value={newTask.definition_of_done}
                onChange={(e) => setNewTask(prev => ({ ...prev, definition_of_done: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                data-testid="task-dod-input"
              />
            </div>
            <Button onClick={handleCreateTask} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="create-task-btn">
              Crear Tarea
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Nueva Nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <span className="text-sm text-gray-500 mb-2 block">
                KR: {selectedKRForNote?.title}
              </span>
            </div>
            <Select 
              value={newNote.note_type} 
              onValueChange={(v) => setNewNote(prev => ({ ...prev, note_type: v }))}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="Tipo de nota" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blocker">Bloqueo</SelectItem>
                <SelectItem value="evidence">Evidencia</SelectItem>
                <SelectItem value="proposal">Propuesta</SelectItem>
                <SelectItem value="decision">Decisión</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Contenido de la nota"
              value={newNote.content}
              onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300 min-h-[120px]"
              data-testid="note-content-input"
            />
            <Button onClick={handleCreateNote} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="create-note-btn">
              Crear Nota
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Weekly Plan Dialog */}
      <Dialog open={showWeeklyPlanDialog} onOpenChange={setShowWeeklyPlanDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Sparkles className="w-5 h-5 text-blue-500" />
              Plan Semanal - {company?.code}
            </DialogTitle>
          </DialogHeader>
          {weeklyPlan && (
            <div className="space-y-6">
              {/* Focus Areas */}
              {weeklyPlan.focus_areas && (
                <div>
                  <h3 className="text-sm font-medium text-blue-600 mb-2">Áreas de Foco</h3>
                  <ul className="space-y-1">
                    {weeklyPlan.focus_areas.map((area, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Priority KRs */}
              {weeklyPlan.priority_krs && (
                <div>
                  <h3 className="text-sm font-medium text-amber-600 mb-2">KRs Prioritarios</h3>
                  <ul className="space-y-1">
                    {weeklyPlan.priority_krs.map((kr, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-amber-600">{idx + 1}.</span>
                        {typeof kr === 'string' ? kr : kr.title || kr.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Daily Breakdown */}
              {weeklyPlan.daily_breakdown && (
                <div>
                  <h3 className="text-sm font-medium text-emerald-600 mb-2">Distribución Diaria</h3>
                  <div className="space-y-2">
                    {Object.entries(weeklyPlan.daily_breakdown).map(([day, tasks]) => (
                      <div key={day} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-sm font-medium text-gray-900 mb-1">{day}</p>
                        <ul className="space-y-1">
                          {(Array.isArray(tasks) ? tasks : [tasks]).map((task, idx) => (
                            <li key={idx} className="text-xs text-gray-600">• {typeof task === 'string' ? task : task.title || task}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Milestones */}
              {weeklyPlan.key_milestones && (
                <div>
                  <h3 className="text-sm font-medium text-purple-600 mb-2">Hitos Clave</h3>
                  <ul className="space-y-1">
                    {weeklyPlan.key_milestones.map((milestone, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <Target className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        {typeof milestone === 'string' ? milestone : milestone.title || milestone}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risks */}
              {weeklyPlan.risks && weeklyPlan.risks.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-600 mb-2">Riesgos</h3>
                  <ul className="space-y-1">
                    {weeklyPlan.risks.map((risk, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        {typeof risk === 'string' ? risk : (risk.risk || risk.description || JSON.stringify(risk))}
                        {risk.mitigation && (
                          <span className="text-gray-500 ml-1">→ {risk.mitigation}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create KR Dialog */}
      <Dialog open={showKRDialog} onOpenChange={setShowKRDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Nuevo Key Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <span className="text-sm text-gray-500 mb-2 block">
                Objetivo: {selectedObjectiveForKR?.title}
              </span>
            </div>
            
            {/* AI Generation Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Generar con IA</span>
              </div>
              <Button
                onClick={handleGenerateAIKRs}
                disabled={generatingKRs}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="generate-ai-krs-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generatingKRs ? 'Generando...' : 'Generar Key Results con IA'}
              </Button>
              
              {/* AI Generated KRs */}
              {aiKRs.length > 0 && (
                <div className="mt-4 space-y-2">
                  <span className="text-sm font-medium text-blue-900">Key Results sugeridos:</span>
                  {aiKRs.map((kr, idx) => (
                    <div key={idx} className="p-3 bg-white rounded border border-blue-200">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{kr.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{kr.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>Meta: {kr.target_value} {kr.unit}</span>
                            <span>Actual: {kr.current_value} {kr.unit}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCreateKR(kr)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`create-ai-kr-${idx}`}
                        >
                          Crear
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Creation Section */}
            <div className="border-t border-gray-200 pt-4">
              <span className="text-sm font-medium text-gray-900 mb-3 block">O crear manualmente:</span>
              
              <div className="space-y-4">
                <div>
                  <Input
                    placeholder="Título del Key Result"
                    value={newKR.title}
                    onChange={(e) => setNewKR(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                    data-testid="kr-title-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Descripción"
                    value={newKR.description}
                    onChange={(e) => setNewKR(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                    data-testid="kr-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Input
                      type="number"
                      placeholder="Valor meta"
                      value={newKR.target_value}
                      onChange={(e) => setNewKR(prev => ({ ...prev, target_value: parseFloat(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                      data-testid="kr-target-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Valor actual"
                      value={newKR.current_value}
                      onChange={(e) => setNewKR(prev => ({ ...prev, current_value: parseFloat(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                      data-testid="kr-current-input"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Unidad (%)"
                      value={newKR.unit}
                      onChange={(e) => setNewKR(prev => ({ ...prev, unit: e.target.value }))}
                      className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                      data-testid="kr-unit-input"
                    />
                  </div>
                </div>
                <div>
                  <Select 
                    value={newKR.visibility} 
                    onValueChange={(v) => setNewKR(prev => ({ ...prev, visibility: v }))}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Visibilidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Público</SelectItem>
                      <SelectItem value="private">Privado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={() => handleCreateKR()} 
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white" 
                  data-testid="create-kr-btn"
                >
                  Crear Key Result
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Objective Dialog */}
      <Dialog open={showObjectiveDialog} onOpenChange={setShowObjectiveDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Nuevo Objetivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* AI Generation Section */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-900">Generar con IA</span>
              </div>
              <div className="space-y-3">
                <Input
                  placeholder="Área de enfoque (ej: crecimiento, eficiencia, innovación)"
                  value={objectiveFocusArea}
                  onChange={(e) => setObjectiveFocusArea(e.target.value)}
                  className="bg-white border-emerald-200 focus:border-emerald-300"
                  data-testid="objective-focus-input"
                />
                <Button
                  onClick={handleGenerateAIObjectives}
                  disabled={generatingObjectives || !objectiveFocusArea}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="generate-ai-objectives-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generatingObjectives ? 'Generando...' : 'Generar Objetivos con IA'}
                </Button>
              </div>
              
              {/* AI Generated Objectives */}
              {aiObjectives.length > 0 && (
                <div className="mt-4 space-y-2">
                  <span className="text-sm font-medium text-emerald-900">Objetivos sugeridos:</span>
                  {aiObjectives.map((objective, idx) => (
                    <div key={idx} className="p-3 bg-white rounded border border-emerald-200">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{objective.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{objective.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>{objective.quarter} {objective.year}</span>
                            <span className="capitalize">{objective.visibility}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCreateObjective(objective)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`create-ai-objective-${idx}`}
                        >
                          Crear
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Creation Section */}
            <div className="border-t border-gray-200 pt-4">
              <span className="text-sm font-medium text-gray-900 mb-3 block">O crear manualmente:</span>
              
              <div className="space-y-4">
                <div>
                  <Input
                    placeholder="Título del objetivo"
                    value={newObjective.title}
                    onChange={(e) => setNewObjective(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                    data-testid="objective-title-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Descripción"
                    value={newObjective.description}
                    onChange={(e) => setNewObjective(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                    data-testid="objective-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Select 
                      value={newObjective.quarter} 
                      onValueChange={(v) => setNewObjective(prev => ({ ...prev, quarter: v }))}
                    >
                      <SelectTrigger className="bg-gray-50 border-gray-200">
                        <SelectValue placeholder="Trimestre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Q1">Q1</SelectItem>
                        <SelectItem value="Q2">Q2</SelectItem>
                        <SelectItem value="Q3">Q3</SelectItem>
                        <SelectItem value="Q4">Q4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Año"
                      value={newObjective.year}
                      onChange={(e) => setNewObjective(prev => ({ ...prev, year: parseInt(e.target.value) || 2026 }))}
                      className="bg-gray-50 border-gray-200 focus:bg-white focus:border-gray-300"
                      data-testid="objective-year-input"
                    />
                  </div>
                  <div>
                    <Select 
                      value={newObjective.visibility} 
                      onValueChange={(v) => setNewObjective(prev => ({ ...prev, visibility: v }))}
                    >
                      <SelectTrigger className="bg-gray-50 border-gray-200">
                        <SelectValue placeholder="Visibilidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Público</SelectItem>
                        <SelectItem value="private">Privado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button 
                  onClick={() => handleCreateObjective()} 
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white" 
                  data-testid="create-objective-btn"
                >
                  Crear Objetivo
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTaskForDetail}
        open={showTaskDetailModal}
        onClose={() => {
          setShowTaskDetailModal(false);
          setSelectedTaskForDetail(null);
        }}
        onUpdate={handleTaskUpdate}
        onDelete={handleTaskDelete}
      />
    </div>
  );
};

export default ScorecardPage;
